/*
 *  ************************************************************************
 *  *  © [2015 - 2024] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import AdyenModal from '../../adyen-modal';
import set from 'lodash/set';
import get from 'lodash/get';
import { Concrete } from './layouts/concrete';
import { Lavish } from './layouts/lavish';
import React from 'react';
import { Minimalist } from './layouts/minimalist';
import { LoginlessFlat } from './layouts/loginless-flat';
import { LoginlessGrouped } from './layouts/loginless-grouped';
import { isPbPreview } from './layout-utils';
import { getPath, getSubscriptionRedirect } from '../../../utils';

export const getRedirectURL = ({ config }) => {
  const sketchesHost = get(config, ['sketches-host'], '');
  const whiteListedUrlParams = get(config, ['pagebuilder-config', 'general', 'others', 'whiteListedUrlParams'], []);

  const redirectUrl = getSubscriptionRedirect();
  const defaultParams = redirectUrl ? `redirectUrl=${redirectUrl}` : '';

  return getPath({
    sketchesHost,
    relativePath: '/subscription-success',
    defaultParams,
    injectCustomParams: true,
    whiteListedUrlParams,
    swapDefaultCustomParams: true
  });
};

export const navigateToSubscriptionSuccessPage = (props) => {
  const redirectUrl = getRedirectURL(props);
  if (props?.navigateTo) {
    return props.navigateTo(redirectUrl);
  }
  if (typeof window !== 'undefined') {
    window.location.href = redirectUrl;
  }
};

export const getSupportedRecurringPaymentProvider = plan => {
  const paymentGateways = get(plan, ['supported_payment_providers'], []);
  const supportedGateways = [];
  const planMetadata = get(plan, ['metadata'], {});
  const currency = get(plan, 'price_currency');

  paymentGateways.forEach(pg => {
    // adyen and paytrail are auto linked and they won't appear in metadata
    if (pg === 'paytrail' || pg === 'adyen') {
      supportedGateways.push(pg);
      return;
    }
    const isLinkedPaymentGateway = get(planMetadata, [pg, 'plan_id', currency], false);
    if (isLinkedPaymentGateway) supportedGateways.push(pg);
  });

  return supportedGateways[0]; // TODO: return whole array when we support multiple pgs
};

export const openPaymentScreen = (plan = {}, props) => {
  const paymentGateway = plan?.recurring
    ? getSupportedRecurringPaymentProvider(plan)
    : get(plan, ['supported_payment_providers', 0], 'razorpay');

  if (!paymentGateway) {
    console.error('No payment gateway available to initiate payment');
    return null;
  }

  if (props?.captureShippingAddress) {
    const userMetaData = props?.userMetaData || {};
    const { line1, line2, city, country, pincode, state } = userMetaData?.address;
    const { dialCode, email } = userMetaData?.other;
    const formattedMetadata = {
      ...plan.metadata,
      phone_number: userMetaData?.['phone_number'],
      name: `${line1}`,
      address: `${line2}`,
      city: `${city}`,
      country: `${country}`,
      pin_code: `${pincode}`,
      state: `${state}`,
      dial_code: `${dialCode}`,
      email: `${email}`
    };
    set(plan, ['metadata'], formattedMetadata);
  }
  const sketchesHost = get(props, ['config', 'sketches-host'], '');
  const whiteListedUrlParams = get(props, ["config", 'pagebuilder-config', 'general', 'others', 'whiteListedUrlParams'], []);

  switch (paymentGateway) {
    case 'razorpay':
      return props.initRazorPayPayment(plan, 'standard');
    case 'paypal':
      const paypalOptions = {
        selectedPlan: plan,
        planType: 'standard',
        returnUrl: getPath({
          sketchesHost,
          absolutePath: `${document.location.origin}/paypal-payment-success`,
          injectCustomParams: true,
          whiteListedUrlParams
        }),
        cancelUrl: getPath({
          sketchesHost,
          absolutePath: `${document.location.origin}/paypal-payment-failed`,
          injectCustomParams: true,
          whiteListedUrlParams
        })
      };
      return props.initPaypalPayment(paypalOptions);
    case 'omise':
      return props.initOmisePayment(plan, 'standard');
    case 'paytrail':
      const paytrailOptions = {
        selectedPlan: plan,
        planType: 'standard',
        returnUrl: getPath({
          sketchesHost,
          absolutePath: `${document.location.origin}/paytrail-success.html`,
          injectCustomParams: true,
          whiteListedUrlParams
        }),
        cancelUrl: getPath({
          sketchesHost,
          absolutePath: `${document.location.origin}/paytrail-cancel.html`,
          injectCustomParams: true,
          whiteListedUrlParams
        })
      };
      return props.initPaytrailPayment(paytrailOptions);
    case 'adyen':
      const locale = get(
        props,
        ['config', 'pagebuilder-config', 'general', 'accesstypeIntegration', 'locale'],
        'en-US'
      );
      return props.initAdyenPayment(plan, 'standard', AdyenModal, locale);
    case 'stripe':
      return props.initStripePayment({
        argType: 'options',
        selectedPlan: plan,
        planType: 'standard',
        successUrl: getPath({
          sketchesHost,
          absolutePath: `${document.location.origin}${getRedirectURL(props)}`,
          injectCustomParams: true,
          whiteListedUrlParams
        }),
        cancelUrl: getPath({
          sketchesHost,
          absolutePath: `${document.location.origin}/subscription`,
          injectCustomParams: true,
          whiteListedUrlParams
        })
      });
    default:
      console.error(`Payment gateway '${paymentGateway}' is not supported yet`);
  }
};

export const getSubscriptionLayout = (props) => {
  const rows = get(props, ['config', 'pagebuilder-config', 'subscription', 'rows'], []);
  const subRow =
    rows.find((r) => ['Concrete', 'Minimalist', 'LoginlessFlat', 'LoginlessGrouped', 'Lavish'].includes(r?.layout)) ||
    rows[0] ||
    {};
  let layout = subRow.layout || 'Lavish';
  const pageSetting = get(props, ['config', 'pagebuilder-config', 'subscription', 'pageSetting'], {});
  const enableLoginlessSubscription = get(pageSetting, ['enableLoginlessSubscription'], false);

  const isPB = isPbPreview(props);

  if (!isPB && !enableLoginlessSubscription && ['LoginlessFlat', 'LoginlessGrouped'].includes(layout)) {
    layout = 'Lavish';
  }

  switch (layout) {
    case 'Concrete':
      return <Concrete {...props} />;
    case 'Minimalist':
      return <Minimalist {...props} />;
    case 'LoginlessFlat':
      return <LoginlessFlat {...props} />;
    case 'LoginlessGrouped':
      return <LoginlessGrouped {...props} />;
    default:
      return <Lavish {...props} />;
  }
};
